type DurationSelectorState = {
  visible: boolean;
  options: number[];
  date: string | null;
  time: string | null;
  slotInfo: Record<string, unknown> | null;
  startInfo: Record<string, unknown> | null;
  prices: DurationPriceMap;
  loading: boolean;
};

type PriceRaw = Record<string, unknown> & {
  GrossAmount?: { ToString?: () => string } | null;
  grossAmount?: { ToString?: () => string } | null;
  GrossAmountStr?: unknown;
  grossAmountStr?: unknown;
};

type DurationPriceEntry = Record<string, unknown> & {
  label?: unknown;
  raw?: PriceRaw | null;
  GrossAmountStr?: unknown;
  grossAmountStr?: unknown;
  GrossAmount?: { ToString?: () => string } | null;
  grossAmount?: { ToString?: () => string } | null;
};

type DurationPriceMap = Record<number, { label: string | null; raw: PriceRaw | null }>;

type SlotInfoWithPrices = Record<string, unknown> & {
  priceByDuration?: Record<string, DurationPriceEntry> | null;
};

export function getDefaultDurationSelectorState(): DurationSelectorState {
  return {
    visible: false,
    options: [],
    date: null,
    time: null,
    slotInfo: null,
    startInfo: null,
    prices: {},
    loading: false,
  };
}

export function normalizeDurationOptions(options: Array<number | string> | null | undefined): number[] {
  if (!Array.isArray(options)) {
    return [];
  }

  const normalized = options
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value) && value > 0);

  return Array.from(new Set(normalized));
}

export function buildDurationPriceMap(slotInfo: Record<string, unknown> | null | undefined): DurationPriceMap {
  const priceMap: DurationPriceMap = {};
  if (!slotInfo || typeof slotInfo !== 'object') {
    return priceMap;
  }

  const rawPrices = (slotInfo as SlotInfoWithPrices).priceByDuration;
  if (rawPrices && typeof rawPrices === 'object') {
    Object.entries(rawPrices as Record<string, DurationPriceEntry>).forEach(([key, entry]) => {
      const durationValue = Number(key);
      if (!Number.isFinite(durationValue) || durationValue <= 0) {
        return;
      }

      const entryValue = entry;
      const entryRaw = entryValue.raw ?? null;
      const rawGrossAmount = entryRaw?.GrossAmount ?? null;
      const rawNetAmount = entryRaw?.grossAmount ?? null;
      const label = (entryValue.label ?? null)
        || (entryRaw?.GrossAmountStr ?? null)
        || (entryRaw?.grossAmountStr ?? null)
        || (rawGrossAmount && typeof rawGrossAmount.ToString === 'function' ? rawGrossAmount.ToString() : null)
        || (rawNetAmount && typeof rawNetAmount.ToString === 'function' ? rawNetAmount.ToString() : null)
        || null;

      priceMap[durationValue] = {
        label: label !== undefined && label !== null ? String(label) : null,
        raw: entryRaw || null,
      };
    });
  }

  return priceMap;
}

type DurationSelectorContext = {
  date?: string | null;
  time?: string | null;
  slotInfo?: Record<string, unknown> | null;
  startInfo?: Record<string, unknown> | null;
};

export function buildDurationSelectorState(
  options: Array<number | string> | null | undefined,
  context: DurationSelectorContext | null | undefined,
): DurationSelectorState {
  const normalizedOptions = normalizeDurationOptions(options);
  if (!normalizedOptions.length) {
    return getDefaultDurationSelectorState();
  }

  const slotInfo = context && context.slotInfo ? context.slotInfo : null;
  const prices = buildDurationPriceMap(slotInfo);

  return {
    visible: true,
    options: normalizedOptions,
    date: context && context.date ? context.date : null,
    time: context && context.time ? context.time : null,
    slotInfo,
    startInfo: context && context.startInfo ? context.startInfo : null,
    prices,
    loading: false,
  };
}

export function resolveDurationPriceLabel(
  duration: number | string,
  priceMap: DurationPriceMap | null | undefined,
  slotInfo: Record<string, unknown> | null | undefined,
): string | null {
  const numericDuration = Number(duration);
  const slotInfoPrices = slotInfo && (slotInfo as SlotInfoWithPrices).priceByDuration
    ? (slotInfo as SlotInfoWithPrices).priceByDuration
    : null;
  const entry = (priceMap && priceMap[numericDuration])
    || (slotInfoPrices ? slotInfoPrices[String(numericDuration)] : null);

  if (!entry) {
    return null;
  }

  const entryValue = entry as DurationPriceEntry;
  const entryRaw = entryValue.raw ?? null;
  const label = (entryValue.label ?? null)
    || (entryValue.GrossAmountStr ?? null)
    || (entryValue.grossAmountStr ?? null)
    || (entryRaw?.GrossAmountStr ?? null)
    || (entryRaw?.grossAmountStr ?? null)
    || null;

  return label !== undefined && label !== null ? String(label) : null;
}
